import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, X, RotateCcw } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@/components/ui/visually-hidden';

interface RenderGalleryProps {
  images: string[];
  unitNumber: string;
  rotationAngle?: number;
}

export const RenderGallery = ({ images, unitNumber, rotationAngle = 0 }: RenderGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const touchStartDistance = useRef<number | null>(null);
  const touchStartZoom = useRef<number>(1);
  
  // Preview zoom/pan state
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const [isPreviewPanning, setIsPreviewPanning] = useState(false);
  const [previewPanStart, setPreviewPanStart] = useState({ x: 0, y: 0 });
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const previewTouchStartDistance = useRef<number | null>(null);
  const previewTouchStartZoom = useRef<number>(1);
  const previewHasPanned = useRef<boolean>(false);

  if (!images || images.length === 0) {
    return null;
  }

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
    trackEvent('render_gallery_thumbnail_click', {
      unit_number: unitNumber,
      image_index: index,
    });
  };

  const handleLargeImageClick = () => {
    setIsFullscreenOpen(true);
    trackEvent('render_gallery_large_click', {
      unit_number: unitNumber,
      image_index: selectedIndex,
    });
  };

  const handlePrev = () => {
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setSelectedIndex((prev) => (prev + 1) % images.length);
  };

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.5, 5));
    trackEvent('render_gallery_zoom', { unit_number: unitNumber, action: 'zoom_in' });
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.5, 1));
    trackEvent('render_gallery_zoom', { unit_number: unitNumber, action: 'zoom_out' });
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    trackEvent('render_gallery_zoom', { unit_number: unitNumber, action: 'reset' });
  };

  // Preview zoom handlers
  const handlePreviewZoomIn = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPreviewZoom((prev) => Math.min(prev + 0.5, 3));
  };

  const handlePreviewZoomOut = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPreviewZoom((prev) => Math.max(prev - 0.5, 1));
  };

  const handlePreviewResetZoom = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
  };

  // Reset zoom when image changes
  useEffect(() => {
    if (isFullscreenOpen) {
      setZoom(1);
      setPan({ x: 0, y: 0 });
    }
    // Reset preview zoom when image changes
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
  }, [selectedIndex, isFullscreenOpen]);

  // Mouse wheel zoom for fullscreen
  useEffect(() => {
    if (!isFullscreenOpen || !imageContainerRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((prev) => {
        const newZoom = Math.max(1, Math.min(5, prev + delta));
        return newZoom;
      });
    };

    const container = imageContainerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [isFullscreenOpen]);

  // Mouse wheel zoom for preview
  useEffect(() => {
    if (isFullscreenOpen || !previewContainerRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setPreviewZoom((prev) => {
        const newZoom = Math.max(1, Math.min(3, prev + delta));
        return newZoom;
      });
    };

    const container = previewContainerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [isFullscreenOpen]);

  // Mouse pan for fullscreen
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom <= 1) return;
    setIsPanning(true);
    setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanning || zoom <= 1) return;
    setPan({
      x: e.clientX - panStart.x,
      y: e.clientY - panStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  // Preview mouse pan
  const handlePreviewMouseDown = (e: React.MouseEvent) => {
    if (previewZoom <= 1) return;
    e.stopPropagation();
    setIsPreviewPanning(true);
    previewHasPanned.current = false;
    setPreviewPanStart({ x: e.clientX - previewPan.x, y: e.clientY - previewPan.y });
  };

  const handlePreviewMouseMove = (e: React.MouseEvent) => {
    if (!isPreviewPanning || previewZoom <= 1) return;
    e.stopPropagation();
    previewHasPanned.current = true;
    setPreviewPan({
      x: e.clientX - previewPanStart.x,
      y: e.clientY - previewPanStart.y,
    });
  };

  const handlePreviewMouseUp = () => {
    setIsPreviewPanning(false);
    // Reset after a short delay to allow click handler to check
    setTimeout(() => {
      previewHasPanned.current = false;
    }, 100);
  };

  // Touch pan and pinch zoom for fullscreen
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      touchStartDistance.current = distance;
      touchStartZoom.current = zoom;
    } else if (e.touches.length === 1 && zoom > 1) {
      // Single touch pan
      setIsPanning(true);
      setPanStart({ x: e.touches[0].clientX - pan.x, y: e.touches[0].clientY - pan.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStartDistance.current !== null) {
      // Pinch zoom
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      const scale = distance / touchStartDistance.current;
      const newZoom = Math.max(1, Math.min(5, touchStartZoom.current * scale));
      setZoom(newZoom);
    } else if (e.touches.length === 1 && isPanning && zoom > 1) {
      // Single touch pan
      e.preventDefault();
      setPan({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    touchStartDistance.current = null;
    setIsPanning(false);
  };

  // Preview touch pan and pinch zoom
  const handlePreviewTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch zoom
      e.stopPropagation();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      previewTouchStartDistance.current = distance;
      previewTouchStartZoom.current = previewZoom;
    } else if (e.touches.length === 1 && previewZoom > 1) {
      // Single touch pan
      e.stopPropagation();
      setIsPreviewPanning(true);
      setPreviewPanStart({ x: e.touches[0].clientX - previewPan.x, y: e.touches[0].clientY - previewPan.y });
    }
  };

  const handlePreviewTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && previewTouchStartDistance.current !== null) {
      // Pinch zoom
      e.preventDefault();
      e.stopPropagation();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      const scale = distance / previewTouchStartDistance.current;
      const newZoom = Math.max(1, Math.min(3, previewTouchStartZoom.current * scale));
      setPreviewZoom(newZoom);
    } else if (e.touches.length === 1 && isPreviewPanning && previewZoom > 1) {
      // Single touch pan
      e.preventDefault();
      e.stopPropagation();
      setPreviewPan({
        x: e.touches[0].clientX - previewPanStart.x,
        y: e.touches[0].clientY - previewPanStart.y,
      });
    }
  };

  const handlePreviewTouchEnd = () => {
    previewTouchStartDistance.current = null;
    setIsPreviewPanning(false);
  };

  // Keyboard navigation in fullscreen
  useEffect(() => {
    if (!isFullscreenOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && !e.shiftKey) {
        setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
      } else if (e.key === 'ArrowRight' && !e.shiftKey) {
        setSelectedIndex((prev) => (prev + 1) % images.length);
      } else if (e.key === 'Escape') {
        setIsFullscreenOpen(false);
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      } else if (e.key === '0') {
        handleResetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreenOpen, images.length]);

  return (
    <>
      <Card className="mb-8">
        <CardContent className="p-4 sm:p-6">
          <h2 className="font-display text-xl font-semibold mb-4">3D Interior Views</h2>
          
          {/* Side-by-side layout on desktop, stacked on mobile */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Main Preview */}
            <div className="flex-1 lg:flex-[2]">
              <div
                ref={previewContainerRef}
                className="relative w-full rounded-lg overflow-hidden bg-muted cursor-pointer group"
                onClick={(e) => {
                  // Don't open fullscreen if clicking on zoom controls
                  if ((e.target as HTMLElement).closest('.preview-zoom-controls')) {
                    return;
                  }
                  // Don't open fullscreen if user was panning (not a click)
                  if (previewHasPanned.current) {
                    return;
                  }
                  handleLargeImageClick();
                }}
                onMouseDown={handlePreviewMouseDown}
                onMouseMove={handlePreviewMouseMove}
                onMouseUp={handlePreviewMouseUp}
                onMouseLeave={handlePreviewMouseUp}
                onTouchStart={handlePreviewTouchStart}
                onTouchMove={handlePreviewTouchMove}
                onTouchEnd={handlePreviewTouchEnd}
                style={{
                  cursor: previewZoom > 1 ? (isPreviewPanning ? 'grabbing' : 'grab') : 'pointer',
                }}
              >
                <div
                  className="relative w-full h-[300px] lg:h-[350px] flex items-center justify-center overflow-hidden"
                  style={{
                    transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                    transition: isPreviewPanning ? 'none' : 'transform 0.2s ease-out',
                    transformOrigin: 'center center',
                  }}
                >
                  <img
                    src={images[selectedIndex]}
                    alt={`3D render ${selectedIndex + 1} of Unit ${unitNumber}`}
                    className="max-w-full max-h-full object-contain select-none"
                    draggable={false}
                    style={{ transform: rotationAngle ? `rotate(${rotationAngle}deg)` : undefined }}
                  />
                </div>
                
                {/* Zoom overlay - only show when not zoomed */}
                {previewZoom <= 1 && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                )}
                
                {/* Preview zoom controls */}
                <div className="preview-zoom-controls absolute top-2 right-2 z-10 flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-black/60 hover:bg-black/80 text-white h-7 w-7"
                    onClick={handlePreviewZoomIn}
                    disabled={previewZoom >= 3}
                  >
                    <ZoomIn className="h-3.5 w-3.5" />
                    <span className="sr-only">Zoom in</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-black/60 hover:bg-black/80 text-white h-7 w-7"
                    onClick={handlePreviewZoomOut}
                    disabled={previewZoom <= 1}
                  >
                    <ZoomOut className="h-3.5 w-3.5" />
                    <span className="sr-only">Zoom out</span>
                  </Button>
                  {previewZoom > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="bg-black/60 hover:bg-black/80 text-white h-7 w-7"
                      onClick={handlePreviewResetZoom}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      <span className="sr-only">Reset zoom</span>
                    </Button>
                  )}
                </div>
                
                {/* Navigation arrows */}
                {images.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white h-8 w-8 z-10"
                      onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white h-8 w-8 z-10"
                      onClick={(e) => { e.stopPropagation(); handleNext(); }}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </>
                )}
                
                {/* Image counter and zoom level */}
                <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
                  {images.length > 1 && (
                    <div className="bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {selectedIndex + 1} / {images.length}
                    </div>
                  )}
                  {previewZoom > 1 && (
                    <div className="bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {Math.round(previewZoom * 100)}%
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Thumbnail strip - horizontal scroll on mobile, vertical on desktop */}
            {images.length > 1 && (
              <div className="lg:flex-1 lg:max-w-[180px]">
                <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto lg:max-h-[350px] pb-2 lg:pb-0 lg:pr-2">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => handleThumbnailClick(index)}
                      className={`relative flex-shrink-0 w-20 h-14 lg:w-full lg:h-auto lg:aspect-video rounded-md overflow-hidden border-2 transition-all flex items-center justify-center ${
                        index === selectedIndex
                          ? 'border-primary ring-1 ring-primary'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`3D render thumbnail ${index + 1}`}
                        className="max-w-full max-h-full object-contain"
                        style={{ transform: rotationAngle ? `rotate(${rotationAngle}deg)` : undefined }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Full-screen Lightbox */}
      <Dialog open={isFullscreenOpen} onOpenChange={setIsFullscreenOpen}>
        <DialogContent className="max-w-[100vw] max-h-[100vh] w-full h-full p-0 gap-0 border-0 bg-black/95">
          <VisuallyHidden>
            <DialogTitle>3D render gallery — Unit {unitNumber}</DialogTitle>
          </VisuallyHidden>
          <div className="relative w-full h-full flex items-center justify-center overflow-hidden">
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-50 bg-black/60 hover:bg-black/80 text-white h-10 w-10"
              onClick={() => setIsFullscreenOpen(false)}
            >
              <X className="h-6 w-6" />
              <span className="sr-only">Close</span>
            </Button>

            {/* Zoom controls */}
            <div className="absolute top-4 left-4 z-50 flex flex-col gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="bg-black/60 hover:bg-black/80 text-white h-10 w-10"
                onClick={handleZoomIn}
                disabled={zoom >= 5}
              >
                <ZoomIn className="h-5 w-5" />
                <span className="sr-only">Zoom in</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="bg-black/60 hover:bg-black/80 text-white h-10 w-10"
                onClick={handleZoomOut}
                disabled={zoom <= 1}
              >
                <ZoomOut className="h-5 w-5" />
                <span className="sr-only">Zoom out</span>
              </Button>
              {zoom > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-black/60 hover:bg-black/80 text-white h-10 w-10"
                  onClick={handleResetZoom}
                >
                  <RotateCcw className="h-5 w-5" />
                  <span className="sr-only">Reset zoom</span>
                </Button>
              )}
            </div>

            {/* Main image container with zoom/pan */}
            <div
              ref={imageContainerRef}
              className="relative w-full h-full flex items-center justify-center p-4 sm:p-8 md:p-12 overflow-hidden"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default',
              }}
            >
              <div
                style={{
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transition: isPanning ? 'none' : 'transform 0.2s ease-out',
                  transformOrigin: 'center center',
                }}
              >
                <img
                  src={images[selectedIndex]}
                  alt={`3D render ${selectedIndex + 1} of Unit ${unitNumber}`}
                  className="max-w-full max-h-full object-contain select-none"
                  draggable={false}
                  style={{ transform: rotationAngle ? `rotate(${rotationAngle}deg)` : undefined }}
                />
              </div>
            </div>

            {/* Navigation arrows */}
            {images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-50 bg-black/60 hover:bg-black/80 text-white h-12 w-12 sm:h-14 sm:w-14"
                  onClick={handlePrev}
                >
                  <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7" />
                  <span className="sr-only">Previous image</span>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-50 bg-black/60 hover:bg-black/80 text-white h-12 w-12 sm:h-14 sm:w-14"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-6 w-6 sm:h-7 sm:w-7" />
                  <span className="sr-only">Next image</span>
                </Button>
              </>
            )}

            {/* Image counter and zoom level */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3">
              {images.length > 1 && (
                <div className="bg-black/60 text-white text-sm px-4 py-2 rounded-full">
                  {selectedIndex + 1} / {images.length}
                </div>
              )}
              {zoom > 1 && (
                <div className="bg-black/60 text-white text-sm px-4 py-2 rounded-full">
                  {Math.round(zoom * 100)}%
                </div>
              )}
            </div>

            {/* Thumbnail strip at bottom (desktop only) */}
            {images.length > 1 && (
              <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-50 hidden md:block">
                <div className="flex gap-2 max-w-[90vw] overflow-x-auto px-4 py-2 bg-black/40 rounded-lg backdrop-blur-sm">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedIndex(index)}
                      className={`relative flex-shrink-0 w-16 h-10 rounded overflow-hidden border-2 transition-all flex items-center justify-center ${
                        index === selectedIndex
                          ? 'border-white ring-2 ring-white'
                          : 'border-white/30 hover:border-white/60'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="max-w-full max-h-full object-contain"
                        style={{ transform: rotationAngle ? `rotate(${rotationAngle}deg)` : undefined }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
